import { Cubit } from "blac-next";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import reportErrorSentry from "src/lib/reportErrorSentry";

export enum LoadingKey {
  payment = "payment",
  none = "_",
  login = "login",
  register = "register",
  verifyEmail = "verifyEmail",
  requestPassReset = "requestPassReset",
  updatePass = "updatePass",
  saveFormData = "saveFormData",
  messages = "messages",
  treatmentPlan = "treatmentPlan",
  treatmentPlanFrequency = "treatmentPlanFrequency",
  treatmentPlanLabTest = "treatmentPlanLabTest",
  shippingAddress = "shippingAddress",
  avatar = "avatar",
  shipment = "shipment",
  paymentRedirect = "paymentRedirect",
  changePassword = "changePassword",
  number = "number",
  upload = "upload",
  suggestion = "suggestion",
  labResults = "labResults",
  userUploads = "labUploads",
  labOrders = "labOrders",
  subscriptionState = "subscriptionState",
  saveQuestionnaireResult = "saveQuestionnaireResult",
  questionnaire = "questionnaire",
  initiateVerification = "initiateVerification",
  answeredQuestionnaires = "answeredQuestionnaires",
  updateSuggestion = "updateSuggestion",
  coupon = "coupon",
  referral = "referral",
  userPreferences = "userPreferences",
  userConditions = "userConditions",
  sanityData = "sanityData",
  exampleSubscription = "exampleSubscription",
  requestSubscriptionChange = "requestSubscriptionChange",
  PAYMENT_METHOD_SETUP = "PAYMENT_METHOD_SETUP",
  medications = "medications",
  submitLabResults = "submitLabResults",
  profileData = "profileData",
  checkingZipCode = "checkingZipCode",
  authCheck = "authCheck",
  loadingLabResults = "loadingLabResults",
  loadingObservationBundles = "loadingObservationBundles",
  loadingObservationBundle = "loadingObservationBundle",
  checkCustomSignupStepDataComplete = "checkCustomSignupStepDataComplete",
  logout = "logout",
  devices = "devices",
  consistencyScore = "consistencyScore"
}

export default class LoadingCubit extends Cubit<string[]> {
  static keepAlive = true;
  instantState: string[] = [];
  updateDebuff: ReturnType<typeof setTimeout> | undefined;
  documentLoaded = false;
  windowLoaded = false;

  // Long-running loading detection (10 second threshold)
  private loadingTimers: Map<LoadingKey, ReturnType<typeof setTimeout>> =
    new Map();
  private loadingStartTimes: Map<LoadingKey, number> = new Map();
  private readonly LONG_LOADING_THRESHOLD_MS = 10000; // 10 seconds

  constructor() {
    super([]);

    document.addEventListener("DOMContentLoaded", () => {
      this.documentLoaded = true;
      this.updateState();
    });

    window.addEventListener("load", () => {
      this.windowLoaded = true;
      this.updateState();
    });
  }

  reset = (): void => {
    this.instantState = [];
    this.updateState();
    this.emit([]);

    // Clear all long-running loading timers
    this.loadingTimers.forEach((timer) => clearTimeout(timer));
    this.loadingTimers.clear();
    this.loadingStartTimes.clear();
  };

  updateState = (): void => {
    if (this.updateDebuff) {
      clearTimeout(this.updateDebuff);
    }
    this.updateDebuff = setTimeout(() => {
      const changed = this.state.join(",") !== this.instantState.join(",");
      if (changed) {
        this.emit([...this.instantState]);
      }
    });
  };

  public readonly start = (key: LoadingKey): void => {
    const isLoading = this.instantState.indexOf(key) !== -1;
    if (isLoading) {
      return;
    }
    this.instantState.push(key);
    this.updateState();

    // Track start time for long-running loading detection
    const startTime = Date.now();
    this.loadingStartTimes.set(key, startTime);

    // Set timer to report if loading exceeds threshold
    const timer = setTimeout(() => {
      this.reportLongRunningLoading(key, startTime);
    }, this.LONG_LOADING_THRESHOLD_MS);

    this.loadingTimers.set(key, timer);
  };

  public readonly finish = (key: LoadingKey): void => {
    const isLoading = this.instantState.indexOf(key) !== -1;
    if (!isLoading) {
      return;
    }
    this.instantState = this.instantState.filter(
      (k) => (k as LoadingKey) !== key
    );
    this.updateState();

    // Clear timer if loading finishes before threshold
    const timer = this.loadingTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.loadingTimers.delete(key);
    }
    this.loadingStartTimes.delete(key);
  };

  public readonly finishAllLoadings = () => {
    addSentryBreadcrumb("loading", "Finishing all loading states");

    this.instantState = [];
    this.updateState();

    // Clear all long-running loading timers
    this.loadingTimers.forEach((timer) => clearTimeout(timer));
    this.loadingTimers.clear();
    this.loadingStartTimes.clear();
  };

  public readonly isLoading = (key?: LoadingKey): boolean => {
    if (key) {
      return this.state.indexOf(key) !== -1;
    }
    return this.state.length > 0;
  };

  get appHasLoaded(): boolean {
    return this.documentLoaded && this.windowLoaded;
  }

  /**
   * Reports long-running loading states to Sentry
   * Includes graceful error handling to prevent monitoring from breaking app functionality
   */
  private reportLongRunningLoading(key: LoadingKey, startTime: number): void {
    try {
      const duration = Date.now() - startTime;

      // Add breadcrumb for context
      addSentryBreadcrumb(
        "performance",
        `Loading state "${key}" exceeded ${this.LONG_LOADING_THRESHOLD_MS / 1000} seconds`,
        "warning",
        {
          loadingKey: key,
          durationMs: duration,
          durationSeconds: Math.round(duration / 1000)
        }
      );

      reportErrorSentry(
        `Long-running loading state: ${key}`,
        {
          loadingKey: key,
          durationMs: duration,
          durationSeconds: Math.round(duration / 1000),
          timestamp: new Date().toISOString(),
          activeLoadingKeys: [...this.instantState],
          totalActiveLoadings: this.instantState.length
        },
        { expected: true }
      );
    } catch (error) {
      // Silently fail to prevent monitoring from breaking the app
      // eslint-disable-next-line no-console
      console.warn("Failed to report long-running loading state:", error);
    }
  }
}
